import { useMutation } from '@tanstack/react-query';
import { CONFIG } from '@/lib/config/config';
import { processApiResponseReactQuery } from '@/lib/api/process-api-response-react-query.helper';
import { requestApi } from '@/lib/api/request-api.helper';
import { pushAnalyticsEvent } from '@/lib/helpers/analytics/analytics.helper';
import { MixpanelWrapper } from '@/lib/mixpanel/useMixpanel';
import { MIXPANEL_EVENTS } from '@/lib/mixpanel/mixpanel-events.constants';
import { useSignUpStore } from '../../useSignUp.store';

type RequestNames =
  | 'activate-and-login'
  | 'activate'
  | 'remove-coupon'
  | 'apply-coupon-code'
  | 'resend-activation-mail'
  | 'resend-password';

export const useUserRequestMutation = () => {
  const url = `${CONFIG.API.ENDPOINT}${CONFIG.API.METHODS.USER_ACTIONS}`;
  const setSuccesActivateAccount = useSignUpStore((state) => state.setSuccesActivateAccount);
  const setSSOToken = useSignUpStore((state) => state.setSSOToken);

  return useMutation({
    mutationKey: ['user-action'],
    mutationFn: async ({
      payload,
      name,
      withoutSession
    }: {
      payload: unknown;
      name: RequestNames;
      withoutSession?: boolean;
    }) => {
      const response = await requestApi.post({
        url,
        body: { payload, name },
        withoutSession
      });
      if (response?.status === 201) {
        const newResponse = response.clone();
        // we cannot call the response.json() twice, then we are using the response.clone() that exists for this purpose to make that possible
        const newResponseJson = await newResponse.json();

        if (name === 'activate') {
          setSuccesActivateAccount();
        }
        if (name === 'activate-and-login') {
          const sso_token = newResponseJson.data.sso_token;
          setSuccesActivateAccount();
          setSSOToken(sso_token);
        }

        if (name === 'activate' || name === 'activate-and-login') {
          MixpanelWrapper.track(MIXPANEL_EVENTS.USER_ACTIVATED, {
            sso_token: newResponseJson?.data?.sso_token
          });

          pushAnalyticsEvent({
            event: 'email_verified',
            user_data: {
              user_id: newResponseJson?.data?._id,
              email_address: newResponseJson?.data?.email
            }
          });
        }
      }

      return await processApiResponseReactQuery({ response });
    }
  });
};
